import { useCallback } from 'react';

import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useTranslation } from '../../../shared/language';

import { AppStorage } from '../../../service/UserStorage';
import {
  BusinessLocationService,
  RegisterInvoiceBusinessLocation,
} from '../../danh-muc/dia-diem-kinh-doanh/BusinessLocationService';

/** Ký tự đầu ký hiệu hóa đơn (invoiceSerial) — loại 2, 5, 6, 7 cần gán địa điểm kinh doanh (NĐ 141/2026/NĐ-CP) */
export const INVOICE_SERIAL_PREFIXES_REQUIRING_BUSINESS_LOCATION = ['2', '5', '6', '7'] as const;

const ROUTE_BUSINESS_LOCATION_CATALOG = '/danh-muc/dia-diem-kinh-doanh';
const ROUTE_REGISTER_INVOICE_MANAGEMENT = '/phat-hanh/mau-hoa-don';

export const getIsHouseholdBusinessFromStorage = (): boolean => {
  const flag = AppStorage.get('isHouseholdBusiness');
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  const raw = AppStorage.get('tenantInfo');
  if (!raw) return false;
  try {
    return JSON.parse(raw).isHouseholdBusiness === true;
  } catch {
    return false;
  }
};

/** Điều kiện 1: Ký hiệu thuộc loại 2, 5, 6, 7 */
export const isInvoiceSerialTypeRequiringBusinessLocation = (symbolCode?: string): boolean => {
  const prefix = String(symbolCode ?? '').charAt(0);
  return (INVOICE_SERIAL_PREFIXES_REQUIRING_BUSINESS_LOCATION as readonly string[]).includes(prefix);
};

/** Điều kiện 2: isHouseholdBusiness = true (HKD/CNKD) */
export const isHouseholdBusinessTaxpayer = (): boolean => getIsHouseholdBusinessFromStorage();

/** Điều kiện 1 + 2 đồng thời — mới kiểm tra địa điểm kinh doanh (điều kiện 3) */
export const shouldApplyHouseholdBusinessLocationRule = (symbolCode?: string): boolean =>
  isHouseholdBusinessTaxpayer() && isInvoiceSerialTypeRequiringBusinessLocation(symbolCode);

type ItemWithBusinessLocation = {
  invoiceSerial?: string;
  businessLocationCode?: string;
  businessLocationName?: string;
  businessLocationAddress?: string;
};

const isBusinessLocationFieldEmpty = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  return !value;
};

/**
 * Điều kiện 3: đủ 3 trường ĐĐKD (code, name, address).
 * Trả true khi thiếu (không đáp ứng đủ 3 điều kiện tổng) — cần hiện dialog / chặn thao tác.
 */
export const isHouseholdBusinessInvoiceMissingBusinessLocation = (
  item: ItemWithBusinessLocation | null | undefined,
  registerInvoiceSymbolCode?: string
): boolean => false;

export const buildBusinessLocationItemFromApi = (
  symbolCode: string | undefined,
  data: RegisterInvoiceBusinessLocation | null | undefined
): ItemWithBusinessLocation => ({
  invoiceSerial: symbolCode,
  businessLocationCode: data?.code ?? '',
  businessLocationName: data?.name ?? '',
  businessLocationAddress: data?.address ?? '',
});

/**
 * Giống DialogCrup (mergeBusinessLocationFromApi + kiểm tra):
 * gọi API getBusinessLocation rồi đánh giá đủ 3 điều kiện hay không.
 */
export const resolveHouseholdBusinessLocationBlocked = async (
  registerInvoiceId: string | undefined,
  symbolCode?: string
): Promise<boolean> => false;

export type HouseholdBusinessLocationNoticeDialogProps = {
  visible: boolean;
  onHide: () => void;
};

const navigateTo = (path: string) => {
  window.location.hash = path;
};

const NoticeLink = ({ label, path }: { label: string; path: string }) => (
  <button
    type="button"
    className="p-0 border-none bg-transparent cursor-pointer inline-flex align-items-center gap-1 text-primary font-semibold line-height-3"
    onClick={() => navigateTo(path)}
  >
    <span>{label}</span>
    <i className="pi pi-external-link text-sm" aria-hidden />
  </button>
);

export const HouseholdBusinessLocationNoticeDialog = ({
  visible,
  onHide,
}: HouseholdBusinessLocationNoticeDialogProps) => {
  const { t } = useTranslation();
  return null;

  const handleClose = useCallback(() => {
    onHide();
  }, [onHide]);

  const header = (
    <div className="flex align-items-center justify-content-between w-full pr-1">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bell text-yellow-500 text-2xl" aria-hidden />
        <span className="font-semibold text-900 text-lg">{t('common.notice')}</span>
      </div>
      <button
        type="button"
        className="p-dialog-header-icon p-dialog-header-close p-link border-circle w-2rem h-2rem flex align-items-center justify-content-center"
        aria-label={t('common.close')}
        onClick={handleClose}
      >
        <i className="pi pi-times text-600" aria-hidden />
      </button>
    </div>
  );

  const footer = (
    <div className="flex align-items-center justify-content-end w-full gap-2">
      <Button type="button" label={t('common.understood')} className="p-button-sm" onClick={handleClose} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header={header}
      footer={footer}
      modal
      draggable={false}
      resizable={false}
      closable={false}
      baseZIndex={3000}
      style={{ width: 'min(640px, 94vw)' }}
      className="household-business-location-notice"
      appendTo={typeof document !== 'undefined' ? document.body : undefined}
    >
     <div className="line-height-3 text-800">
        <h3 className="mt-0 mb-3 text-primary font-semibold text-xl">
          {t('common.businessLocationDeclarationGuide')}
        </h3>

        <p className="font-bold mb-2 mt-0">
          Theo khoản 3 Phụ lục "NỘI DUNG CỦA HÓA ĐƠN" Nghị định 254/2026/NĐ-CP:
        </p>

        <p className="mt-0 mb-3">
          Trường hợp hộ kinh doanh, cá nhân kinh doanh thuộc đối tượng sử dụng
          hóa đơn điện tử và sử dụng mã số thuế của hộ kinh doanh, cá nhân kinh
          doanh cho tất cả các cửa hàng thì phải thể hiện rõ tên, mã, địa chỉ
          địa điểm kinh doanh trên hóa đơn.
        </p>

        {/* <div
          className="flex gap-2 p-3 mb-3 border-round"
          style={{ backgroundColor: '#e8f4fc', borderLeft: '4px solid #2196f3' }}
        >
          <i className="pi pi-lightbulb text-yellow-500 text-xl flex-shrink-0 mt-1" aria-hidden />
          <p className="m-0">
            <strong>Lưu ý:</strong> Nếu chỉ có một địa điểm kinh doanh, bạn <strong>không bắt buộc</strong> phải khai
            báo mã địa điểm kinh doanh. <strong>Khuyến nghị</strong> thực hiện khai báo để thuận tiện khi mở rộng thêm
            chi nhánh hoặc cửa hàng trong tương lai.
          </p>
        </div> */}

        <h4
          className="mt-0 mb-2 font-semibold text-lg"
          style={{ color: "#c45c26" }}
        >
          Các bước hướng dẫn thực hiện
        </h4>

        <ul className="m-0 pl-3" style={{ listStyleType: "disc" }}>
          <li className="mb-2">
            <span className="font-semibold">{t('common.step', { number: 1 })}</span> {t('common.declareBusinessLocations')}
            địa điểm kinh doanh →{" "}
            <NoticeLink
              label={t('catalog.businessLocation')}
              path={ROUTE_BUSINESS_LOCATION_CATALOG}
            />
          </li>
          <li className="mb-2">
            <span className="font-semibold">{t('common.step', { number: 2 })}</span> {t('common.assignBusinessLocationToSymbol')}
            hiệu hóa đơn →{" "}
            <NoticeLink
              label={t('common.invoiceSymbolManagement')}
              path={ROUTE_REGISTER_INVOICE_MANAGEMENT}
            />
          </li>
          <li>
            <span className="font-semibold">{t('common.step', { number: 3 })} {t('common.createInvoice')}</span>
          </li>
        </ul>
      </div>
    </Dialog>
  );
};
